import { useEffect, useState } from 'react';
import type { AgingBucket, AgingResponse, Kpi, PublicUser, Reminder } from '@otter/shared';
import { api } from '../lib/api';
import { fmt } from '../lib/format';
import { hasPermission } from '../lib/perm';

type Props = {
  user: PublicUser;
  scope: string;
  onOpenInvoice: (id: string) => void;
};

type TimelinePoint = { month: number; amount: number };

const BUCKET_ORDER: AgingBucket[] = ['al-dia', '1-30', '31-60', '61-90', '90+'];
const BUCKET_LABELS: Record<AgingBucket, string> = {
  'al-dia': 'Al día',
  '1-30': '1-30',
  '31-60': '31-60',
  '61-90': '61-90',
  '90+': '90+',
};

function bucketClass(b: AgingBucket): string {
  return b === 'al-dia' ? 'at-dia' : `b${b.replace('+', '')}`;
}

export function Dashboard({ scope, user, onOpenInvoice }: Props) {
  const [kpis, setKpis] = useState<Kpi[]>([]);
  const [kpisSecondary, setKpisSecondary] = useState<Kpi[]>([]);
  const [issued, setIssued] = useState<TimelinePoint[]>([]);
  const [paid, setPaid] = useState<TimelinePoint[]>([]);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [aging, setAging] = useState<AgingResponse | null>(null);
  const [byOwner, setByOwner] = useState<Array<{
    code: string;
    name: string;
    color: string;
    clients_count: number;
    issued: number;
    paid: number;
    overdue: number;
  }>>([]);

  const canSeeTreasury = hasPermission(user, 'treasury.view');
  useEffect(() => {
    void (async () => {
      const q = scope !== 'all' ? { scope } : undefined;
      try {
        const k = await api<{ kpis: Kpi[]; kpisSecondary: Kpi[] }>('/admin/dashboard/kpis', { query: q });
        setKpis(k.kpis);
        setKpisSecondary(k.kpisSecondary ?? []);
        const t = await api<{ issued: TimelinePoint[]; paid: TimelinePoint[] }>(
          '/admin/dashboard/timeline',
          { query: q },
        );
        setIssued(t.issued);
        setPaid(t.paid);
        const r = await api<{ items: Reminder[] }>('/admin/dashboard/reminders', { query: q });
        setReminders(r.items);
        if (canSeeTreasury) {
          const a = await api<AgingResponse>('/admin/treasury/aging', { query: q });
          setAging(a);
        } else {
          setAging(null);
        }
        if (user.isSystemAdmin) {
          const o = await api<{
            items: Array<{
              code: string;
              name: string;
              color: string;
              clients_count: number;
              issued: number;
              paid: number;
              overdue: number;
            }>;
          }>('/admin/dashboard/by-owner');
          setByOwner(o.items);
        }
      } catch {
        /* ignore */
      }
    })();
  }, [scope, user.isSystemAdmin, canSeeTreasury]);

  return (
    <div className="body">
      <div className="pageh">
        <h1>Dashboard</h1>
        <div className="sub">
          scope = <span className="v">{scope}</span>
        </div>
      </div>

      <div className="kpis">
        {kpis.map((k, i) => (
          <div key={k.key} className={`kpi k${i + 1}`}>
            <div className="lbl">{k.label}</div>
            <div className="v">{k.value}</div>
            <div className="s">{k.sub}</div>
          </div>
        ))}
      </div>

      {kpisSecondary.length > 0 && (
        <div className="kpis" style={{ marginTop: 10 }}>
          {kpisSecondary.map((k) => (
            <div key={k.key} className="kpi">
              <div className="lbl">{k.label}</div>
              <div className="v">{k.value}</div>
              <div className="s">{k.sub}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <div className="card">
          <div className="head">
            <h3>Emisión vs cobro</h3>
            <div className="meta">año {new Date().getFullYear()}</div>
          </div>
          <div className="pad">
            <TimelineChart issued={issued} paid={paid} />
          </div>
        </div>
      </div>

      <div className="row2" style={{ marginTop: 12 }}>
        <div className="card">
          <div className="head">
            <h3>Reminders</h3>
            <div className="meta">alertas live</div>
          </div>
          <div className="alerts">
            {reminders.length === 0 && (
              <div className="empty" style={{ padding: '24px 16px' }}>
                Sin alertas, todo limpio
              </div>
            )}
            {reminders.slice(0, 6).map((r, idx) => (
              <div
                key={idx}
                className={`alert-row ${r.severity}`}
                onClick={() => r.invoiceId && onOpenInvoice(r.invoiceId)}
                role="button"
              >
                <span className="dot" />
                <div>
                  <div className="ttl">
                    {r.formattedNumber ?? (r.kind === 'stale_draft' ? 'Borrador antiguo' : '—')}
                  </div>
                  <div className="sub">{r.message}</div>
                </div>
                {r.invoiceId && <span className="arrow">→</span>}
              </div>
            ))}
          </div>
        </div>

        {aging && (
          <div className="card">
            <div className="head">
              <h3>AR aging</h3>
              <div className="meta">{fmt(aging.total)} pendiente</div>
            </div>
            <div className="pad">
              {aging.total > 0 ? (
                <>
                  <div className="aging-bar">
                    {BUCKET_ORDER.map((k) => {
                      const v = aging.buckets[k] ?? 0;
                      if (v <= 0) return null;
                      const pct = (v / aging.total) * 100;
                      return (
                        <div
                          key={k}
                          className={`seg ${bucketClass(k)}`}
                          style={{ width: `${pct}%` }}
                          title={`${BUCKET_LABELS[k]} · ${fmt(v)}`}
                        >
                          {pct > 8 ? `${Math.round(pct)}%` : ''}
                        </div>
                      );
                    })}
                  </div>
                  <div
                    style={{
                      marginTop: 14,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                      fontFamily: 'Geist Mono, monospace',
                      fontSize: 11,
                      color: 'var(--muted)',
                    }}
                  >
                    {BUCKET_ORDER.map((k) => (
                      <div
                        key={k}
                        style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}
                      >
                        <span>{BUCKET_LABELS[k]}</span>
                        <span>
                          {aging.counts[k] ?? 0} fact. · {fmt(aging.buckets[k] ?? 0)}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="empty">Sin facturas pendientes</div>
              )}
            </div>
          </div>
        )}
      </div>

      {user.isSystemAdmin && byOwner.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div className="card">
            <div className="head">
              <h3>Por comercial</h3>
            </div>
            <table className="t">
              <thead>
                <tr>
                  <th>Comercial</th>
                  <th>Clientes</th>
                  <th className="num">Emitido</th>
                  <th className="num">Cobrado</th>
                  <th className="num">Vencido</th>
                </tr>
              </thead>
              <tbody>
                {byOwner.map((o) => (
                  <tr key={o.code}>
                    <td data-label="Comercial">
                      <span className={`av-mini av-${o.color}`}>{o.code}</span> {o.name}
                    </td>
                    <td data-label="Clientes">{o.clients_count}</td>
                    <td className="num" data-label="Emitido">{fmt(o.issued)}</td>
                    <td className="num" data-label="Cobrado">{fmt(o.paid)}</td>
                    <td className="num" data-label="Vencido">{fmt(o.overdue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function TimelineChart({ issued, paid }: { issued: TimelinePoint[]; paid: TimelinePoint[] }) {
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const issuedMap = new Map(issued.map((p) => [p.month, p.amount]));
  const paidMap = new Map(paid.map((p) => [p.month, p.amount]));
  const max = Math.max(
    1,
    ...months.flatMap((m) => [issuedMap.get(m) ?? 0, paidMap.get(m) ?? 0]),
  );
  const W = 720;
  const H = 200;
  const dx = W / 11;
  const points = (map: Map<number, number>) =>
    months.map((m, i) => {
      const v = map.get(m) ?? 0;
      const x = i * dx;
      const y = H - (v / max) * (H - 24) - 8;
      return [x, y] as [number, number];
    });
  const issuedPts = points(issuedMap);
  const paidPts = points(paidMap);
  const path = (pts: Array<[number, number]>) =>
    pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0]} ${p[1]}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 220 }}>
      <path d={path(issuedPts)} fill="none" stroke="var(--accent)" strokeWidth="2" />
      <path
        d={`${path(issuedPts)} L ${W} ${H} L 0 ${H} Z`}
        fill="var(--accent-bg)"
        opacity="0.6"
      />
      <path
        d={path(paidPts)}
        fill="none"
        stroke="var(--green-text)"
        strokeWidth="2"
        strokeDasharray="4,3"
      />
      {months.map((m, i) => (
        <text
          key={m}
          x={i * dx}
          y={H - 1}
          fontSize="9"
          fill="var(--muted)"
          textAnchor="middle"
          fontFamily="Geist Mono, monospace"
        >
          {m}
        </text>
      ))}
    </svg>
  );
}
